import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Lock, CheckCircle2, Gift, Zap, ShoppingCart } from 'lucide-react';
import { Button } from './ui/Button';
interface ExitIntentUpsellProps {
  open: boolean;
  onClose: () => void;
  bundleUrl: string;
  mainOnlyUrl: string;
}
function formatPrice(value: number) {
  return value.toLocaleString('es-AR');
}
export function ExitIntentUpsell({
  open,
  onClose,
  bundleUrl,
  mainOnlyUrl
}: ExitIntentUpsellProps) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  // Math (scanned from page)
  const MAIN = 25000;
  const BUMPS_TOTAL = 25000; // 12.500 × 2
  const TRUE_TOTAL = MAIN + BUMPS_TOTAL; // 50.000
  const BUMPS_DISCOUNTED = Math.round(BUMPS_TOTAL * 0.5); // 12.500 (50% off)
  const BUNDLE_PRICE = MAIN + BUMPS_DISCOUNTED; // 37.500
  return <AnimatePresence data-id="element-109">
      {open && <>
          {/* Local CSS for flash + pulse animations */}
          <style data-id="element-110">{`
            @keyframes mp-flash {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(1.05); }
            }
            @keyframes mp-pulse-glow {
              0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6); }
              50% { box-shadow: 0 0 0 14px rgba(16, 185, 129, 0); }
            }
            .mp-flash { animation: mp-flash 1.1s ease-in-out infinite; }
            .mp-pulse-glow { animation: mp-pulse-glow 1.8s ease-out infinite; }
            @media (prefers-reduced-motion: reduce) {
              .mp-flash, .mp-pulse-glow { animation: none !important; }
            }
          `}</style>

          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 z-[200] bg-stone-900/70 backdrop-blur-sm" data-id="element-111" />

          {/* Modal */}
          <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 overflow-y-auto" data-id="element-112">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 280,
          damping: 28
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg my-8 overflow-hidden" data-id="element-113">
              {/* Close button */}
              <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-600 transition-colors" data-id="element-114">
                <X className="w-4 h-4" data-id="element-115" />
              </button>

              {/* HEADER BANNER */}
              <div className="bg-gradient-to-br from-stone-900 to-stone-800 px-6 py-5 text-center" data-id="element-116">
                <span className="inline-flex items-center gap-1.5 bg-amber-500/20 text-amber-300 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-3 border border-amber-400/30" data-id="element-117">
                  <Sparkles className="w-3 h-3" data-id="element-118" />
                  Oferta única — solo en esta pantalla
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight" data-id="element-119">
                  ¡Esperá! 🛑 No te vayas con las manos a medio llenar
                </h2>
                <p className="text-stone-300 text-sm mt-2" data-id="element-120">
                  Tenemos una propuesta especial antes de que cierres tu pedido…
                </p>
              </div>

              {/* BODY */}
              <div className="px-6 py-6 text-left" data-id="element-121">
                {/* Flashing discount badge */}
                <div className="flex justify-center mb-5" data-id="element-122">
                  <span className="mp-flash inline-flex items-center gap-2 bg-red-100 text-red-700 font-extrabold text-base px-4 py-2 rounded-full border-2 border-red-300" data-id="element-123">
                    <Zap className="w-4 h-4" data-id="element-124" />
                    50% OFF en los Extras
                  </span>
                </div>

                <p className="text-stone-700 text-base leading-relaxed mb-3" data-id="element-125">
                  El Protocolo Anti-Inflamación te lleva en 30 días a recuperar
                  tu abdomen. Pero los <strong data-id="element-126">Extras</strong> son los que
                  aceleran y blindan tus resultados desde el día 1.
                </p>
                <p className="text-stone-600 text-sm mb-5" data-id="element-127">
                  Sumá hoy el pack completo: tenés el plan, el botón de pánico
                  para resultados de un día para el otro, y la guía para no
                  fallar nunca en el súper.
                </p>

                {/* WHAT'S INCLUDED LIST */}
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-5" data-id="element-128">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 mb-3 text-center" data-id="element-129">
                    Tu pack completo incluye:
                  </h3>
                  <ul className="space-y-2.5" data-id="element-130">
                    <li className="flex items-start gap-2.5 text-sm text-stone-700" data-id="element-131">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" data-id="element-132" />
                      <span data-id="element-133">
                        <strong className="text-stone-900" data-id="element-134">
                          Protocolo Anti-Inflamación
                        </strong>{' '}
                        — Plan 30 días + Reset 72hs
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-stone-700" data-id="element-135">
                      <Gift className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" data-id="element-136" />
                      <span data-id="element-137">
                        <strong data-id="element-138">3 Bonos GRATIS:</strong> Menú Express, Tracker
                        Abdomen Calmado, Guía Café Sin Inflamar
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-stone-700" data-id="element-139">
                      <Zap className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" data-id="element-140" />
                      <span data-id="element-141">
                        <strong data-id="element-142">Extra — Protocolo SOS 24hs:</strong> tu botón de
                        pánico para deshincharte de un día para el otro.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-stone-700" data-id="element-143">
                      <ShoppingCart className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" data-id="element-144" />
                      <span data-id="element-145">
                        <strong data-id="element-146">Extra — El Súper Anti-Inflamatorio:</strong>{' '}
                        guía de compras inteligente para comer real, barato y
                        sin inflamarte.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* PRICING BLOCK */}
                <div className="text-center mb-5" data-id="element-147">
                  <div className="flex items-center justify-center gap-3 mb-1" data-id="element-148">
                    <span className="text-stone-400 line-through font-semibold text-lg" data-id="element-149">
                      ${formatPrice(TRUE_TOTAL)}
                    </span>
                    <span className="bg-red-100 text-red-700 font-bold text-xs px-2 py-0.5 rounded-full" data-id="element-150">
                      AHORRÁS ${formatPrice(TRUE_TOTAL - BUNDLE_PRICE)}
                    </span>
                  </div>
                  <div className="text-4xl md:text-5xl font-extrabold text-emerald-600 mb-1" data-id="element-151">
                    ${formatPrice(BUNDLE_PRICE)}{' '}
                    <span className="text-lg text-stone-400 font-medium" data-id="element-152">
                      ARS
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center justify-center gap-1.5 mt-2" data-id="element-153">
                    <Lock className="w-3 h-3" data-id="element-154" />
                    Precio especial — solo disponible en esta pantalla
                  </p>
                </div>

                {/* CTA BUTTONS */}
                <div className="space-y-3" data-id="element-155">
                  <div className="mp-pulse-glow rounded-xl" data-id="element-156">
                    <Button href={bundleUrl} className="w-full text-center text-base py-4" size="lg" data-id="element-157">
                      ¡SÍ! Quiero el pack completo por $
                      {formatPrice(BUNDLE_PRICE)}
                    </Button>
                  </div>
                  <a href={mainOnlyUrl} className="block w-full text-center text-sm text-stone-500 hover:text-stone-700 underline underline-offset-4 py-2 transition-colors" data-id="element-158">
                    No gracias, continuar sin los extras →
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}